// Category-aware fallback/demo images for events & news entries.
//
// Rationale: unrelated events (a techno rave and a food workshop) should
// never show the same stock photo. This module groups a curated set of
// locally-hosted, realistic photographs (real nightlife/social/culture
// scenes with people and atmosphere — not illustrations, icons or abstract
// graphics) by theme and picks one deterministically based on the event's
// genres/moods and its own id.
//
// Images are bundled in `public/event-images/` as JPGs (originally sourced
// from Unsplash, downloaded once and committed) rather than hotlinked from a
// third-party host at runtime, so the fallback never depends on external
// network access, rate limits, or a remote host disappearing.

const local = (file) => `${process.env.PUBLIC_URL || ""}/event-images/${file}`;

export const IMAGE_CATEGORIES = {
  // Techno / house / electronic / drum & bass — underground club & rave imagery.
  techno: [local("techno-1.jpg"), local("techno-2.jpg"), local("techno-3.jpg")],
  // Hip-hop / pop / rock / jazz / reggae — live concert & stage imagery.
  liveMusic: [local("liveMusic-1.jpg"), local("liveMusic-2.jpg")],
  // Art workshops / exhibitions — gallery imagery.
  art: [local("art-1.jpg"), local("art-2.jpg")],
  // Food workshops — street food / shared-dining imagery.
  food: [local("food-1.jpg"), local("food-2.jpg")],
  // Adventure mood / outdoor events — hiking & nature-gathering imagery.
  outdoor: [local("outdoor-1.jpg"), local("outdoor-2.jpg")],
  // Festival mood — outdoor festival crowd & stage imagery.
  festival: [local("festival-1.jpg"), local("festival-2.jpg")],
  // Chill mood — campfire / relaxed evening imagery.
  chill: [local("chill-1.jpg"), local("chill-2.jpg")],
  // Creative mood / creative workshops — hands-on studio/painting imagery.
  creative: [local("creative-1.jpg"), local("creative-2.jpg")],
  // Party mood — house-party / friends-celebrating imagery.
  party: [local("party-1.jpg"), local("party-2.jpg")],
  // Romantic mood — rooftop/sunset couple imagery.
  romantic: [local("romantic-1.jpg"), local("romantic-2.jpg")],
  // Social / networking mood — friends at a bar/cafe imagery.
  social: [local("social-1.jpg"), local("social-2.jpg")],
  // Fallback used when no genre/mood maps to a more specific category.
  general: [local("general-1.jpg"), local("general-2.jpg")],
};

// Genre -> category. Keys match the labels in `GENRE_GROUPS` (config/eventOptions.js).
const GENRE_CATEGORY_MAP = {
  House: "techno",
  Techno: "techno",
  Electronic: "techno",
  "Drum & Bass": "techno",
  "Hip-Hop": "liveMusic",
  Pop: "liveMusic",
  Rock: "liveMusic",
  Jazz: "liveMusic",
  Reggae: "liveMusic",
  "Art Workshop": "art",
  "Food Workshop": "food",
  "Chill Workshop": "chill",
  "Creative Workshop": "creative",
};

// Mood -> category. Keys match `MOOD_OPTIONS` (config/eventOptions.js).
const MOOD_CATEGORY_MAP = {
  Chill: "chill",
  Romantic: "romantic",
  Party: "party",
  Social: "social",
  Adventure: "outdoor",
  Music: "liveMusic",
  Creative: "creative",
  Networking: "social",
};

// Picks the most specific matching category from an event's genres, then its
// moods, falling back to "general" when nothing matches.
export const resolveImageCategory = (genres = [], moods = []) => {
  for (const genre of genres) {
    const category = GENRE_CATEGORY_MAP[genre];
    if (category) return category;
  }
  for (const mood of moods) {
    const category = MOOD_CATEGORY_MAP[mood];
    if (category) return category;
  }
  return "general";
};

// Small, fast, deterministic string hash (djb2 variant) — good enough to
// distribute event IDs across a small image pool without needing anything
// cryptographic. Same id always produces the same hash, in any session.
const hashString = (value) => {
  let hash = 5381;
  for (const char of String(value || "")) {
    hash = (hash * 33) ^ char.charCodeAt(0);
  }
  return Math.abs(hash);
};

// Deterministic fallback image for an event/news entry: picking is based
// SOLELY on the event's own id (and its genre/mood-derived category), never
// on list position, pagination cursor, or render order. That guarantees the
// exact same fallback image everywhere the event appears (Events feed, Event
// Details, Chat event previews) and across refreshes/sessions, while still
// letting different events in the same category land on different images
// from that category's pool.
export const getEventFallbackImage = ({ id, genres = [], moods = [] } = {}) => {
  const category = resolveImageCategory(genres, moods);
  const pool = IMAGE_CATEGORIES[category] || IMAGE_CATEGORIES.general;
  const index = hashString(id) % pool.length;
  return pool[index];
};
